import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Detail as DetailComponent, InfoHeader } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import authHeader from 'src/mtska-frontend-data-provider/redux/authHeader';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { CircularProgress } from '@mui/joy';
import { Backdrop } from "@mui/material";

const dataProviderName = 'propertymanagement/property';
const model = 'propertymanagement/property';
const modelExposedName = 'property';
const wizardAction = 'createFromScraping';
const endpointBase = process.env.REACT_APP_API_ENDPOINT || window.location.origin + '/api';

const resolveAgentId = (agent) => {
    if (!agent) return null;
    if (typeof agent === 'string') return agent;
    return agent._id ?? agent.id ?? null;
};

const previewPropertyScraping = (data) => axios({
    url: `${endpointBase}/propertymanagement/property/preview-scraping`,
    method: 'POST',
    headers: authHeader(),
    data,
}).then((response) => response.data);

const CreateFromScraping = (params) => {
    const { t } = useTranslation();
    const { existingModules, userCapabilities } = useContext(AppContext);

    const [drawable, setDrawable] = useState(false);
    const [localError, setLocalError] = useState();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewNotice, setPreviewNotice] = useState();
    const [wizardValues, setWizardValues] = useState({ urlAgency: '' });
    const stepOneRef = useRef({ agent: null, urlAgency: '', owner: '' });
    const { doAction, item, loadOne, errorMessage, errors, saving, success } = useDataProvider(dataProviderName, params.multipart);
    const { scrapingCreateConfig } = existingModules[dataProviderName];

    useEffect(() => {
        loadOne(null).then(() => setDrawable(true));
    }, []);

    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (success && editing && item?.data?.id) {
            (async () => {
                try {
                    await params.handleClose(params.uid, item.data);
                } catch (e) { }
            })();
        }
    }, [success]);

    const onProcessStepAction = (currentStep, stepTotal, actionInvoked, getValues) => {
        setLocalError();
        setPreviewNotice();

        if (actionInvoked < 0) {
            return true;
        }

        const values = getValues();

        if (currentStep === 0 && actionInvoked > 0) {
            if (!values.urlAgency) {
                setLocalError(t('Enter the published property URL'));
                return false;
            }
            if (!values.agent) {
                setLocalError(t('Select an agent'));
                return false;
            }

            setPreviewLoading(true);

            return previewPropertyScraping(values)
                .then((response) => {
                    const owner = values.owner?.trim()
                        ? values.owner
                        : (response.data?.owner ?? '');

                    stepOneRef.current = {
                        agent: values.agent,
                        urlAgency: values.urlAgency,
                        owner,
                    };

                    if (response.reimport) {
                        setPreviewNotice(t('Property already exists. Filled fields were kept; only empty fields were updated from the agency page.'));
                    }

                    setWizardValues({
                        ...values,
                        ...response.data,
                        urlAgency: values.urlAgency,
                        agent: values.agent,
                        owner,
                    });
                    return { type: 'PREVIEW_SUCCESS' };
                })
                .catch((error) => {
                    const message = error?.response?.data?.message
                        ?? error?.message
                        ?? t('Unable to extract data from the provided URL');
                    setLocalError(message);
                    throw error;
                })
                .finally(() => setPreviewLoading(false));
        }

        if (currentStep === 1 && actionInvoked > 0) {
            const agent = values.agent ?? stepOneRef.current.agent ?? wizardValues.agent;
            const urlAgency = values.urlAgency ?? stepOneRef.current.urlAgency ?? wizardValues.urlAgency;
            const owner = values.owner ?? stepOneRef.current.owner ?? wizardValues.owner;

            const id = values.id ?? wizardValues.id;

            return doAction({
                action: wizardAction,
                model,
                data: {
                    ...values,
                    id,
                    urlAgency,
                    owner,
                    agent: resolveAgentId(agent) ?? agent,
                    scrapedImageUrls: values.scrapedImageUrls ?? wizardValues.scrapedImageUrls ?? [],
                },
            });
        }

        return true;
    };

    const onValidateStepAction = (currentStep, getValues, setValue, setError, resultValue) => {
        if (currentStep === 0) {
            return resultValue?.type === 'PREVIEW_SUCCESS';
        }

        if (currentStep === 1) {
            return resultValue?.type === 'ACTION_SUCCESS';
        }

        return true;
    };

    const hasPreviewImages = Array.isArray(wizardValues?.scrapedImageUrls) && wizardValues.scrapedImageUrls.length > 0;

    return (
        !drawable ? <Backdrop open={!drawable} style={{ backgroundColor: "white" }}><CircularProgress /></Backdrop> :
            <DetailComponent
                disableDetailConfigOverride={true}
                values={wizardValues}
                modelName={model.split('/')[1]}
                detailConfig={scrapingCreateConfig}
                dataProviderName={dataProviderName}
                onEditing={setEditing}
                saving={saving || previewLoading}
                errors={errors}
                errorMessage={errorMessage}
                alertMessage={localError}
                title={t('Import {{itemname}} from web', { itemname: t(modelExposedName) })}
                canSave={userCapabilities && userCapabilities[dataProviderName + '.createfromscraping']}
                header={
                    hasPreviewImages ? (
                        <InfoHeader
                            view="property"
                            item={wizardValues}
                            title={t('Import {{itemname}} from web', { itemname: t(modelExposedName) })}
                            subtitle={previewNotice ?? t('Review the scraped property data before saving.')}
                        />
                    ) : (
                        <InfoHeader
                            view="wizard"
                            title={t('Import {{itemname}} from web', { itemname: t(modelExposedName) })}
                            subtitle={previewNotice ?? t('Enter the published property URL, the owner and assign an agent. Property data will be extracted automatically.')}
                        />
                    )
                }
                onProcessStepAction={onProcessStepAction}
                onValidateStepAction={onValidateStepAction}
                {...params}
            />
    );
};

export default CreateFromScraping;
